import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';

const ExamCellDashboard = ({ onNavigate, userInfo }) => {
    // ====================================================================================================
    // STATE MANAGEMENT
    // ====================================================================================================
    const [isMobile, setIsMobile] = useState(false);
    const [loading, setLoading] = useState(true);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [selectedPeriod, setSelectedPeriod] = useState('This Semester');

    // Notifications Panel State
    const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);

    // Exam Cell user data
    const [examCellData, setExamCellData] = useState({
        name: userInfo?.name || "Exam Cell Officer",
        staffId: userInfo?.staffId || "EC001",
        department: "Examination Cell",
        designation: "Exam Officer",
        lastLogin: "2026-01-16 09:15 AM"
    });

    // Dashboard statistics (Enhanced)
    const [stats, setStats] = useState({
        totalStudents: 2450,
        examFormsSubmitted: 1823,
        examFormsPending: 420,
        admitCardsGenerated: 1650,
        pendingFees: 275,
        grievancesOpen: 15,
        upcomingExamsCount: 4,
        approvalRate: 85
    });

    // Recent Activity Feed
    const [activities, setActivities] = useState([
        { id: 1, type: 'form_submission', user: 'Rahul Kumar', action: 'submitted Exam Form', time: '10 mins ago', icon: '📝' },
        { id: 2, type: 'fee_payment', user: 'Priya Sharma', action: 'paid Semester Fees', time: '25 mins ago', icon: '💰' },
        { id: 3, type: 'grievance', user: 'Amit Patel', action: 'raised a Grievance', time: '1 hour ago', icon: '❗' },
        { id: 4, type: 'admit_card', user: 'System', action: 'generated 50 Admit Cards', time: '2 hours ago', icon: '🤖' },
        { id: 5, type: 'login', user: 'Admin', action: 'logged into the portal', time: '3 hours ago', icon: '🔐' },
    ]);

    // Recent exam forms data
    const [recentExamForms] = useState([
        { id: 1, studentId: 'STU001', studentName: 'Rahul Kumar', class: 'BCA 3rd Sem', semester: '3rd', submittedDate: '15/01/2026', status: 'Pending' },
        { id: 2, studentId: 'STU002', studentName: 'Priya Sharma', class: 'BCA 3rd Sem', semester: '3rd', submittedDate: '15/01/2026', status: 'Approved' },
        { id: 3, studentId: 'STU003', studentName: 'Amit Patel', class: 'MCA 1st Sem', semester: '1st', submittedDate: '14/01/2026', status: 'Approved' },
        { id: 4, studentId: 'STU004', studentName: 'Sneha Gupta', class: 'BCA 5th Sem', semester: '5th', submittedDate: '14/01/2026', status: 'Pending' },
        { id: 5, studentId: 'STU005', studentName: 'Vikram Singh', class: 'MCA 3rd Sem', semester: '3rd', submittedDate: '13/01/2026', status: 'Rejected' },
        { id: 6, studentId: 'STU006', studentName: 'Anjali Das', class: 'MBA 1st Sem', semester: '1st', submittedDate: '12/01/2026', status: 'Approved' }
    ]);

    // Upcoming exams schedule
    const [upcomingExams] = useState([
        { id: 1, examName: 'BCA 3rd Semester Mid-Term', date: '25/01/2026', time: '10:00 AM', venue: 'Hall A', students: 450, status: 'Scheduled' },
        { id: 2, examName: 'MCA 1st Semester End-Term', date: '28/01/2026', time: '02:00 PM', venue: 'Hall B', students: 320, status: 'Scheduled' },
        { id: 3, examName: 'BCA 5th Semester Practical', date: '02/02/2026', time: '11:00 AM', venue: 'Lab 1', students: 180, status: 'Tentative' },
        { id: 4, examName: 'MCA 3rd Semester Viva', date: '05/02/2026', time: '09:00 AM', venue: 'Hall C', students: 210, status: 'Tentative' }
    ]);

    // ====================================================================================================
    // EFFECTS
    // ====================================================================================================
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1024);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        setTimeout(() => setLoading(false), 1200);
    }, []);

    // ====================================================================================================
    // HELPERS
    // ====================================================================================================
    const handleNavigation = (page) => {
        const pageMap = {
            'Dashboard': 'examcell-dashboard',
            'Admit Card': 'examcell-admitcard',
            'Admit Format': 'examcell-admitformat',
            'Exam Form': 'examcell-examform',
            'Forms': 'examcell-forms',
            'Semester Fees': 'examcell-semesterfees',
            'Grievance': 'examcell-grievance',
            'Logout': 'login'
        };

        if (onNavigate && pageMap[page]) {
            if (page === 'Logout') {
                localStorage.removeItem('userInfo');
            }
            onNavigate(pageMap[page]);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Approved': return { bg: '#c6f6d5', text: '#22543d' };
            case 'Rejected': return { bg: '#fed7d7', text: '#822727' };
            case 'Pending': return { bg: '#feebc8', text: '#744210' };
            case 'Scheduled': return { bg: '#bee3f8', text: '#2a4365' };
            case 'Tentative': return { bg: '#e2e8f0', text: '#4a5568' };
            default: return { bg: '#edf2f7', text: '#1a202c' };
        }
    };

    // ====================================================================================================
    // LOADING SCREEN
    // ====================================================================================================
    if (loading) {
        return (
            <div style={{
                display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh',
                background: 'linear-gradient(135deg, #238548 0%, #1a5a33 100%)', color: '#ffffff'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{
                        width: '60px', height: '60px', border: '4px solid rgba(255,255,255,0.3)',
                        borderTop: '4px solid #ffffff', borderRadius: '50%',
                        animation: 'spin 1s linear infinite', margin: '0 auto 1rem'
                    }} />
                    <p style={{ fontSize: '1.1rem', fontWeight: '500' }}>Initializing Dashboard...</p>
                </div>
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    // ====================================================================================================
    // MAIN RENDER
    // ====================================================================================================
    return (
        <Layout role="examcell" onNavigate={onNavigate} userInfo={userInfo} activePage="examcell-dashboard">
            {/* Stats Cards */}
            <section style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: '1.5rem',
                marginBottom: '2rem'
            }}>
                {[
                    { icon: '👨‍🎓', title: 'Total Students', value: stats.totalStudents, subtitle: 'Enrolled This Semester' },
                    { icon: '📝', title: 'Exam Forms', value: stats.examFormsSubmitted, subtitle: 'Forms Submitted' },
                    { icon: '⏳', title: 'Pending Approvals', value: stats.examFormsPending, subtitle: 'Awaiting Review' },
                    { icon: '🎫', title: 'Admit Cards', value: stats.admitCardsGenerated, subtitle: 'Already Generated' }
                ].map((stat, index) => (
                    <div key={index} style={{
                        background: '#ffffff',
                        padding: '2rem',
                        borderRadius: '12px',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                        textAlign: 'center',
                        transition: 'all 0.3s ease'
                    }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-5px)';
                            e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                        }}>
                        <div style={{
                            fontSize: '2.5rem',
                            color: '#238548',
                            marginBottom: '1rem'
                        }}>
                            {stat.icon}
                        </div>
                        <h3 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>{stat.title}</h3>
                        <p style={{
                            fontSize: '2.2rem',
                            fontWeight: 'bold',
                            color: '#238548',
                            margin: '0.5rem 0'
                        }}>
                            {stat.value.toLocaleString()}
                        </p>
                        <p style={{ color: '#666', margin: 0 }}>
                            {stat.subtitle}
                        </p>
                    </div>
                ))}
            </section>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: '2rem' }}>
                {/* Recent Forms Table */}
                <div style={{ background: '#ffffff', borderRadius: '12px', padding: '2rem', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }}>
                    <div style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                        <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#333' }}>Recent Form Submissions</h2>
                        <button
                            onClick={() => handleNavigation('Exam Form')}
                            style={{
                                background: 'none', border: 'none', color: '#238548', fontWeight: '600',
                                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem'
                            }}
                        >
                            View All →
                        </button>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #ecf0f1' }}>
                                    <th style={{ padding: '1rem 0.5rem', textAlign: 'left', color: '#666', fontSize: '0.85rem', textTransform: 'uppercase' }}>Student</th>
                                    <th style={{ padding: '1rem 0.5rem', textAlign: 'left', color: '#666', fontSize: '0.85rem', textTransform: 'uppercase' }}>Class</th>
                                    <th style={{ padding: '1rem 0.5rem', textAlign: 'left', color: '#666', fontSize: '0.85rem', textTransform: 'uppercase' }}>Date</th>
                                    <th style={{ padding: '1rem 0.5rem', textAlign: 'center', color: '#666', fontSize: '0.85rem', textTransform: 'uppercase' }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentExamForms.map((form) => {
                                    const statusStyle = getStatusColor(form.status);
                                    return (
                                        <tr key={form.id} style={{ borderBottom: '1px solid #ecf0f1' }}>
                                            <td style={{ padding: '1rem 0.5rem' }}>
                                                <div style={{ fontWeight: '600', color: '#333' }}>{form.studentName}</div>
                                                <div style={{ fontSize: '0.8rem', color: '#666' }}>{form.studentId}</div>
                                            </td>
                                            <td style={{ padding: '1rem 0.5rem', color: '#666' }}>{form.class}</td>
                                            <td style={{ padding: '1rem 0.5rem', color: '#666', fontSize: '0.9rem' }}>{form.submittedDate}</td>
                                            <td style={{ padding: '1rem 0.5rem', textAlign: 'center' }}>
                                                <span style={{
                                                    padding: '0.25rem 0.75rem', borderRadius: '20px', fontSize: '0.8rem',
                                                    fontWeight: '600', backgroundColor: statusStyle.bg, color: statusStyle.text
                                                }}>
                                                    {form.status}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Activity Feed */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                    <div style={{ background: '#ffffff', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', flex: 1 }}>
                        <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.1rem', color: '#333' }}>Live Activity</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                            {activities.map((activity) => (
                                <div key={activity.id} style={{ display: 'flex', gap: '1rem' }}>
                                    <div style={{
                                        width: '36px', height: '36px', borderRadius: '50%', background: '#f0f0f0',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem'
                                    }}>
                                        {activity.icon}
                                    </div>
                                    <div>
                                        <p style={{ margin: 0, fontSize: '0.9rem', color: '#333' }}>
                                            <strong>{activity.user}</strong> {activity.action}
                                        </p>
                                        <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.75rem', color: '#999' }}>{activity.time}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Upcoming Schedule */}
            <div style={{ marginTop: '2rem', background: '#fff', borderRadius: '12px', padding: '2rem', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }}>
                <h2 style={{ margin: '0 0 1.5rem 0', fontSize: '1.25rem', color: '#333' }}>📅 Upcoming Examination Calendar</h2>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #ecf0f1', background: '#f7f7f7' }}>
                                <th style={{ padding: '1rem', textAlign: 'left', color: '#666' }}>Exam Name</th>
                                <th style={{ padding: '1rem', textAlign: 'left', color: '#666' }}>Date & Time</th>
                                <th style={{ padding: '1rem', textAlign: 'left', color: '#666' }}>Venue</th>
                                <th style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>Students</th>
                                <th style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {upcomingExams.map((exam, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #ecf0f1' }}>
                                    <td style={{ padding: '1rem', fontWeight: '500' }}>{exam.examName}</td>
                                    <td style={{ padding: '1rem' }}>{exam.date} <span style={{ color: '#666', fontSize: '0.9rem' }}>at {exam.time}</span></td>
                                    <td style={{ padding: '1rem' }}>{exam.venue}</td>
                                    <td style={{ padding: '1rem', textAlign: 'center' }}>{exam.students}</td>
                                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                                        <span style={{
                                            padding: '0.25rem 0.75rem', borderRadius: '20px', fontSize: '0.8rem',
                                            fontWeight: '600', ...getStatusColor(exam.status)
                                        }}>
                                            {exam.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </Layout>
    );
};

export default ExamCellDashboard;
